import React from 'react';
import { Modal, Btn, COLORS } from '../../ui/crm-ui';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (mode?: 'group-only' | 'everything') => void;
  title: string;
  itemName: string;
  mode: 'corporate-group' | 'family-group' | 'job' | 'entity' | 'document';
  documentMoveDestination?: string;
  childSummary?: string;
}

const DeleteConfirmModal: React.FC<Props> = ({
  open, onClose, onConfirm, title, itemName, mode, documentMoveDestination, childSummary,
}) => {
  const isGroup = mode === 'corporate-group' || mode === 'family-group';

  const spWarning =
    mode === 'document'
      ? 'This will permanently delete the file from SharePoint and cannot be undone.'
      : 'This will permanently delete the SharePoint folder and all its contents. This cannot be undone.';

  const groupOnlyDescription =
    mode === 'corporate-group'
      ? 'Removes this corporate group from the hierarchy. Child family groups become standalone and jobs must be moved first. Documents are moved to General Documents.'
      : 'Removes this family group from the hierarchy. Jobs are reassigned to the parent corporate group. Documents are moved up to the parent group.';

  const everythingDescription =
    mode === 'corporate-group'
      ? 'Permanently deletes this corporate group along with all family groups, jobs, entities and their SharePoint folders.'
      : 'Permanently deletes this family group along with all jobs, entities and their SharePoint folders.';

  const footer = isGroup ? (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, width: '100%' }}>
      {/* Left column: Cancel */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <Btn onClick={onClose} style={{ width: '100%' }}>Cancel</Btn>
      </div>
      {/* Right column: two delete options stacked */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Btn
            onClick={() => onConfirm('group-only')}
            style={{ background: COLORS.orange, color: '#fff', border: 'none', width: '100%' }}
          >
            Delete group only
          </Btn>
          <span style={{ fontSize: 10, color: COLORS.mid, textAlign: 'center', lineHeight: 1.4 }}>
            {groupOnlyDescription}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Btn
            onClick={() => onConfirm('everything')}
            style={{ background: COLORS.red, color: '#fff', border: 'none', width: '100%' }}
          >
            Delete everything
          </Btn>
          <span style={{ fontSize: 10, color: COLORS.mid, textAlign: 'center', lineHeight: 1.4 }}>
            {everythingDescription}
          </span>
        </div>
      </div>
    </div>
  ) : (
    <>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn onClick={() => onConfirm()} style={{ background: COLORS.red, color: '#fff', border: 'none' }}>
        Delete
      </Btn>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title={title} width={500} footer={footer}>
      {/* Warning icon + item name */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 20 }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: COLORS.redSoft, border: `2px solid ${COLORS.red}40`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26, marginBottom: 12,
        }}>
          🗑️
        </div>
        <div style={{ fontSize: 13, color: COLORS.mid, marginBottom: 4 }}>You are about to delete</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: COLORS.dark, wordBreak: 'break-word' }}>
          {itemName}
        </div>
      </div>

      {/* Contextual info blocks */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* SharePoint warning */}
        <div style={{
          background: COLORS.redSoft, border: `1px solid ${COLORS.red}40`,
          borderRadius: 8, padding: '10px 14px',
          fontSize: 12, color: COLORS.red, lineHeight: 1.6,
        }}>
          ⚠️ {spWarning}
        </div>

        {/* Document move destination */}
        {documentMoveDestination && mode !== 'document' && (
          <div style={{
            background: COLORS.orangeSoft, border: `1px solid ${COLORS.orange}40`,
            borderRadius: 8, padding: '10px 14px',
            fontSize: 12, color: COLORS.dark, lineHeight: 1.6,
          }}>
            <strong>Documents will be moved to:</strong> {documentMoveDestination}
          </div>
        )}

        {/* Child summary */}
        {childSummary && (
          <div style={{
            background: COLORS.light, border: `1px solid ${COLORS.border}`,
            borderRadius: 8, padding: '10px 14px',
            fontSize: 12, color: COLORS.dark, lineHeight: 1.6,
          }}>
            <strong>Also contains:</strong> {childSummary}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DeleteConfirmModal;
